// Tiptap-backed rich text editor for the agent composer (BC-23).
// Emits HTML on every change so the consumer can store it directly.
// Disables heading / horizontalRule / codeBlock from StarterKit — the
// allowed subset matches our Loofah sanitizer (see MessageHtmlSanitizer).

import { useEditor, EditorContent, type Editor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Link from '@tiptap/extension-link'
import Image from '@tiptap/extension-image'
import { useEffect } from 'react'
import { cn } from '@/lib/utils'
import { RICH_PROSE_BASE } from '@/lib/richProseClasses'

export interface RichTextEditorHandle {
  editor: Editor | null
}

interface RichTextEditorProps {
  value: string
  onChange: (html: string) => void
  placeholder?: string
  onEditorReady?: (editor: Editor) => void
  className?: string
  /** Inline marks and links only — no lists, quotes, images or code blocks.
   *  Used by short one-or-two-sentence bodies (help-center banners, BC-142),
   *  and matches HelpBannerSanitizer's allowlist. */
  inlineOnly?: boolean
}

const EDITOR_CLASSES = cn(
  'min-h-[140px] max-h-[320px] overflow-y-auto px-4 py-2 text-sm text-foreground focus:outline-none',
  RICH_PROSE_BASE,
  '[&_code]:bg-card/60 [&_blockquote]:border-primary/50 [&_a]:text-primary'
)

export function RichTextEditor({ value, onChange, placeholder, onEditorReady, className, inlineOnly = false }: RichTextEditorProps) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        horizontalRule: false,
        codeBlock: false,
        ...(inlineOnly ? { bulletList: false, orderedList: false, listItem: false, blockquote: false } : {})
      }),
      Link.configure({
        openOnClick: false,
        autolink: true,
        HTMLAttributes: { rel: 'nofollow noopener', target: '_blank' }
      }),
      ...(inlineOnly ? [] : [ Image.configure({ HTMLAttributes: { class: 'rounded-lg max-w-[260px]' } }) ])
    ],
    content: value,
    editorProps: {
      attributes: {
        class: cn(EDITOR_CLASSES, className),
        'data-placeholder': placeholder ?? ''
      }
    },
    // Empty Tiptap docs serialise as `<p></p>` rather than `""`; emit
    // an empty string so the composer's canSubmit check blocks blank sends.
    onUpdate: ({ editor }) => onChange(editor.isEmpty ? '' : editor.getHTML())
  })

  useEffect(() => {
    if (editor && onEditorReady) onEditorReady(editor)
  }, [editor, onEditorReady])

  // Keep external value changes in sync (e.g. clearing after send).
  useEffect(() => {
    if (!editor) return
    if (editor.getHTML() === value) return
    editor.commands.setContent(value, { emitUpdate: false })
  }, [editor, value])

  return <EditorContent editor={editor} />
}
